<template>
  <div>
    <div v-if="!hasDependents">
      <span v-if="emptyValuePlaceholder">{{emptyValuePlaceholder}}</span>
      <span v-else>No dependencies</span>
    </div>
    <template v-else>
      <div
        v-for="(dependencyType, index) in dependencyTypesArray"
        :key="index"
      >
        <div v-if="dependencyType.dependencies.length > 0">
          <h3
            v-if="!hideGroupHeadings"
            style="margin-top:0 !important"
          >{{ decamelize(dependencyType.name) }}</h3>
          <div
            v-for="(dependencies, dependenciesIndex) in dependencyType.dependencies"
            :key="'dependencies' +  dependenciesIndex"
          >
            <strong>{{ code }}</strong>
            is a
            <strong>{{ formatRequisiteType(dependencies[0]) }}</strong>
            for:
            <ul>
              <li
                v-for="(dependency, dependentIndex) in dependencies"
                :key="dependencyType.name + dependenciesIndex + dependentIndex"
              >
                <button
                  v-if="dependency.courseId"
                  class="link cursor-pointer"
                  data-test="showCourseModal"
                  @click="showModal('course', dependency)"
                >
                  {{ getCourseLinkLabel(dependency) }}
                </button>
                <button
                  v-if="dependency.programId"
                  class="link cursor-pointer"
                  data-test="showProgramModal"
                  :to="`/programs/${dependency.programId}`"
                  @click="showModal('program', dependency)"
                >
                  {{ getProgramLinkLabel(dependency) }}
                </button>
                <span v-if="dependency.courseSetId">
                  (from the following course set:
                  <button
                    class="link cursor-pointer"
                    @click="showModal('courseSet', dependency)"
                  >
                    {{ dependency.courseSetName || 'Untitled Course Set' }}
                  </button>
                  )
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
    <CourseSetModal
      :course-set="selectedCourseSet"
      :course-list="courseSetCourseList"
      @close="closeCourseSetModal"
    />
    <ItemDetailsModal
      :details="itemDetails"
      @close="itemDetails = false"
    >
      <template v-if="itemDetails.course">
        <ResourcePreview
          v-if="configurableResourcePreview"
          class="rounded bg-white border border-gray-300 my-4"
          :value="itemDetails.course"
          :template="coursePreviewTemplate"
        />
        <CourseModalCreditsDetails
          v-else-if="showCreditsOnCourseModal"
          :course="itemDetails.course"
          class="mt-4"
        />
      </template>
    </ItemDetailsModal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { decamelize } from '@/utils/generalUtils'
import CourseSetModal from '@/components/CourseSetModal'
import ItemDetailsModal from '@/components/ItemDetailsModal'
import CourseModalCreditsDetails from '@/components/CourseModalCreditsDetails'
import ResourcePreview from '@/components/ResourcePreview'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { CONFIGURABLE_RESOURCE_PREVIEW } from '@/constants/featureFlags'
import {
  courseNameWithFallbacks,
  programNameWithFallbacks,
} from '@/utils/entityNames'

const getCourse = ({ school, courseId, $axios }) =>
  $axios.$get(`/cm/${school}/courses/${encodeURIComponent(courseId)}`)

const getProgram = ({ school, programId, $axios }) =>
  $axios.$get(`/cm/${school}/programs/${programId}`)

export default {
  components: {
    CourseSetModal,
    ItemDetailsModal,
    CourseModalCreditsDetails,
    ResourcePreview,
  },
  props: {
    documentValue: {
      type: Object,
      required: true,
    },
    question: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { configurableResourcePreview } = useFeatureFlags({
      configurableResourcePreview: CONFIGURABLE_RESOURCE_PREVIEW,
    })
    return {
      configurableResourcePreview,
      courseNameWithFallbacks,
      programNameWithFallbacks,
    }
  },
  data() {
    return {
      selectedCourseSet: false,
      courseSetCoursesData: {},
      isLoading: false,
      itemDetails: false,
    }
  },
  computed: {
    ...mapState('settings', ['school', 'coursePreviewTemplate']),
    ...mapGetters('settings', [
      'hideCourseModal',
      'hideProgramModal',
      'showCreditsOnCourseModal',
    ]),
    courseSetCourseList() {
      if (this.isLoading) return []
      if (!this.selectedCourseSet) return []
      const isDynamicCourseSet = this.selectedCourseSet.type === 'dynamic'
      return (
        (isDynamicCourseSet && this.selectedCourseSet.dynamicCourseList) ||
        this.selectedCourseSet.courseList ||
        []
      )
        .map((courseId) => {
          return this.courseSetCoursesData[courseId]
        })
        .filter(Boolean)
    },
    courseDependents() {
      // We must filter out all dependencies that are not supposed to be shown in the public catalog
      const courseDependents = this.documentValue.courseDependents || []
      return courseDependents
        .map((dependentsArray) => {
          return (
            Array.isArray(dependentsArray) &&
            dependentsArray.filter((dependency) => {
              return dependency.showInCatalog
            })
          )
        })
        .filter((dependentsArray) => dependentsArray && dependentsArray.length)
    },
    programDependents() {
      const programDependents = this.documentValue.programDependents || []

      return programDependents
        .map((dependentsArray) => {
          return (
            Array.isArray(dependentsArray) &&
            dependentsArray.filter((dependency) => {
              return dependency.showInCatalog
            })
          )
        })
        .filter((dependentsArray) => dependentsArray && dependentsArray.length)
    },
    dependencyTargetType() {
      return this.$route.name === 'courses-id' ? 'course' : 'program'
    },
    code() {
      const document = this.documentValue
      switch (this.dependencyTargetType) {
        case 'course':
          return (
            document.code ||
            `${document.subjectCode}${document.courseNumber}` ||
            'Course Not Found'
          )
        case 'program':
          return (
            document.code ||
            document.longName ||
            document.name ||
            'Program Not Found'
          )
        default:
          return 'Error getting code'
      }
    },
    dependencyTypesArray() {
      return [
        { dependencies: this.courseDependents, name: 'courses' },
        { dependencies: this.programDependents, name: 'programs' },
      ]
    },
    hasDependents() {
      return this.courseDependents.length || this.programDependents.length
    },
    /*
    This custom config value was added to support a use case by CUNY. CUNY only uses requisites for programs,
    so dependencies are only programs, not courses. In the dependencies card, we by default display a heading
    to separate "program dependencies" from "course dependencies". However, for CUNY, since all dependencies are programs,
    displaying this heading is redundant, so we've made it a configurable item
    */
    hideGroupHeadings() {
      return this.question?.config?.hideGroupHeadings
    },
    emptyValuePlaceholder() {
      return this.question?.config?.emptyValuePlaceholder
    },
  },
  methods: {
    decamelize,
    getDependentLink(dependency, dependencyType) {
      switch (dependencyType) {
        case 'courses':
          return {
            name: 'cmCourse',
            params: { courseId: dependency.courseId },
          }
        case 'programs':
          return {
            name: 'cmProgram',
            params: { programId: dependency.programId },
          }
        case 'courseSets':
          return {
            name: 'cmCourseSets',
          }
      }
    },
    async showModal(type, dependency) {
      if (type === 'course') {
        if (!dependency.courseId) return

        if (this.hideCourseModal) {
          return this.$router.push(`/courses/${encodeURIComponent(dependency.courseGroupId)}`)
        }

        const course = await getCourse({
          courseId: dependency.courseId,
          school: this.school,
          $axios: this.$axios,
        })

        this.itemDetails = {
          path: 'courses',
          course,
          id: dependency.courseGroupId,
          label: this.getCourseLinkLabel(dependency),
          description: course.description,
        }
      }
      if (type === 'program') {
        if (!dependency.programId) return

        if (this.hideProgramModal) {
          return this.$router.push(`/programs/${encodeURIComponent(dependency.programGroupId)}`)
        }

        const program = await getProgram({
          programId: dependency.programId,
          school: this.school,
          $axios: this.$axios,
        })

        this.itemDetails = {
          path: 'programs',
          id: dependency.programGroupId,
          label: this.getProgramLinkLabel(dependency),
          description: program.description,
        }
      } else if (type === 'courseSet') {
        this.showCourseSet(dependency)
      }
    },
    getCourseLinkLabel(course = {}) {
      const code = course.code || `${course.subjectCode + course.courseNumber}`
      const name = courseNameWithFallbacks(course) || 'Untitled Course'
      return `${code} - ${name}`
    },
    getProgramLinkLabel(program = {}) {
      const code = program.code
      const name = programNameWithFallbacks(program) || 'Untitled Program'
      return `${code} - ${name}`
    },
    formatRequisiteType({ type, requisiteType } = {}) {
      if (!type) return 'not found'
      if (type === 'degreeMaps') {
        return 'degree map requirement'
      }

      switch (requisiteType) {
        case 'Completion Requirement':
          return requisiteType.toLowerCase()
        case 'Completion Requirements':
          return requisiteType.toLowerCase().slice(0, requisiteType.length - 1)
        case 'Prerequisite':
          return requisiteType.toLowerCase()
        case 'Prerequisites':
          return requisiteType.toLowerCase().slice(0, requisiteType.length - 1)
        case 'Corequisite':
          return requisiteType.toLowerCase()
        case 'Corequisites':
          return requisiteType.toLowerCase().slice(0, requisiteType.length - 1)
        case 'Antirequisite':
          return requisiteType.toLowerCase()
        case 'Antirequisites':
          return requisiteType.toLowerCase().slice(0, requisiteType.length - 1)
        default:
          return 'requirement'
      }
    },
    showCourseSet(dependency) {
      const courseSet = {
        id: dependency.courseSetId,
        name: dependency.courseSetName,
      }
      this.selectedCourseSet = courseSet
      if (courseSet.id) this.loadCourseSetWithRelatedData(courseSet.id)
    },
    closeCourseSetModal() {
      this.selectedCourseSet = false
      this.courseSetCoursesData = {}
    },
    async loadCourseSetWithRelatedData(courseSetId) {
      this.isLoading = true
      try {
        const data = await this.$axios.$get(
          `/cm/${this.school}/courseSets?courseSetGroupIds=${courseSetId}&includeRelatedData=true`
        )
        if (data.courseSet) this.selectedCourseSet = data.courseSet
        if (data.courses) this.courseSetCoursesData = data.courses
      } catch (e) {
        this.$bugsnag.notify(e)
        console.warn(e)
      }
      this.isLoading = false
    },
  },
}
</script>
